
// utils/createEntityWithOfflineFallback.ts
import { syncService } from '@/services/sync.service';
import { offlineDb } from '@/db/offlineDb';
import { networkService } from '@/services/network.service';
import { v4 as uuid } from 'uuid';
import Dexie from 'dexie';
import { getValidUserId } from '@/utils/validation';

interface CreateEntityOptions<T> {
  entityKey: keyof typeof offlineDb;
  data: Partial<T>;
  supabaseCreateFn: (data: T) => Promise<void>;
  toastSuccessMessage: string;
  toastErrorMessage?: string;
  onAfterCreate?: () => void;
}

export async function createEntityWithOfflineFallback<T extends { id?: string; user_id?: string }>(
  options: CreateEntityOptions<T>
) {
  const {
    entityKey,
    data,
    supabaseCreateFn,
    toastSuccessMessage,
    toastErrorMessage = "Errore durante la creazione",
    onAfterCreate,
  } = options;

  const id = data.id || uuid();
  const userId = getValidUserId();
  if (!userId) {
    throw new Error('Invalid user id');
  }
  const now = new Date().toISOString();

  const enrichedData = {
    ...data,
    id,
    user_id: userId,
    created_at: now,
    updated_at: now
  } as unknown as T;

  try {
    if (networkService.isOnline()) {
      await supabaseCreateFn(enrichedData);
    } else {
      // Add metadata for sync service, but strip it for Dexie
      const syncableData = {
        ...enrichedData,
        _syncStatus: 'pending',
        _lastModified: Date.now()
      };
      
      // Get the correct table and add the data
      const table = offlineDb[entityKey] as any;
      await table.add(syncableData);
      
      // Remove metadata before adding to sync queue for Supabase
      const { _syncStatus, _lastModified, ...dataForSync } = syncableData as any;
      
      // Add to sync queue
      await syncService.addToSyncQueue(entityKey as string, 'create', id, dataForSync);
    }

    if (onAfterCreate) onAfterCreate();

    return {
      success: true,
      message: toastSuccessMessage,
    };
  } catch (err) {
    console.error(`Errore nella creazione di ${entityKey as string}:`, err);
    return {
      success: false,
      message: toastErrorMessage,
      error: err
    };
  }
}
